import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IGame } from "@/redux/games/types";
import { GamesItem } from "../GamesItem";
import { CancelRequestModal } from "../CancelRequestModal";
import { Button } from "@/components";
import { filterLastMonths, groupByDateWithYears } from "@/utils/date/dateGrouping";
import { DateSection } from "@/components/DateSection";
import { YearSeparator } from "@/components/YearSeparator";
import { SwitcherTabBar } from "@/components/SwitcherTabBar";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import {
  fetchCancelApplication,
  fetchGetAppliedGames,
  fetchGetApplicationsCount,
  fetchGetGamesList,
  setGames,
  selectGamesLoading,
} from "@/redux/games";
import { getProfileInfo } from "@/redux/profile/settings/getProfileInfo/selectors";
import { openModal } from "react-url-modal";
import styles from "./MyGamesScreen.module.scss";
import { EmptyGameList } from "../EmptyGameList/EmptyGameList";
import { GameSkeleton } from "../GameSkeleton";

interface MyGamesScreenProps {
  createdGames: IGame[];
  appliedGames: IGame[];
  setIsRequiredAuthOpened: (value: boolean) => void;
}

const SWITCHER_TABS = [
  { key: "enjoying", title: "Вы присоединились" },
  { key: "created", title: "Вы создатель" },
];

export const MyGamesScreen: React.FC<MyGamesScreenProps> = ({
  createdGames,
  appliedGames,
  setIsRequiredAuthOpened,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectGamesLoading);
  const profileInfo = useAppSelector(getProfileInfo);
  const currentUserId = profileInfo.profileInfo?.id;

  const [activeSubTab, setActiveSubTab] = useState("enjoying");
  const [cancelModalGameId, setCancelModalGameId] = useState<number | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  // Последние 6 месяцев (включая прошедшие) — как в мобильном приложении
  const filteredApplied = useMemo(() => filterLastMonths(appliedGames, 6), [appliedGames]);
  const filteredCreated = useMemo(() => filterLastMonths(createdGames, 6), [createdGames]);

  // Группировка с разделителями по годам, обратный хронологический порядок
  const appliedGrouped = useMemo(() => groupByDateWithYears(filteredApplied), [filteredApplied]);
  const createdGrouped = useMemo(() => groupByDateWithYears(filteredCreated), [filteredCreated]);

  const handleCancelModalOpen = (gameId: number) => {
    setCancelModalGameId(gameId);
  };

  const handleCancelModalClose = () => {
    if (!isCancelling) {
      setCancelModalGameId(null);
    }
  };

  const handleCancelApplication = async () => {
    if (!cancelModalGameId) return;

    try {
      setIsCancelling(true);
      await dispatch(fetchCancelApplication(cancelModalGameId)).unwrap();
      const [applied] = await Promise.all([
        dispatch(fetchGetAppliedGames()).unwrap(),
        dispatch(fetchGetApplicationsCount()).unwrap(),
      ]);
      const allGames = await dispatch(fetchGetGamesList()).unwrap();
      const filtered = allGames.filter(
        (game) => !applied.some((appliedGame) => appliedGame.id === game.id)
      );
      dispatch(setGames(filtered));
      setCancelModalGameId(null);
      openModal({ name: "successCancelRequest" });
    } catch (error) {
      console.error("Error cancelling application:", error);
    } finally {
      setIsCancelling(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <SwitcherTabBar
          tabs={SWITCHER_TABS}
          activeKey={activeSubTab}
          onChange={setActiveSubTab}
        />
        <div className={styles.skeletonList}>
          {[...Array(3)].map((_, index) => (
            <GameSkeleton key={index} />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <SwitcherTabBar
        tabs={SWITCHER_TABS}
        activeKey={activeSubTab}
        onChange={setActiveSubTab}
      />

      {activeSubTab === "enjoying" && (
        <>
          {filteredApplied.length === 0 ? (
            <EmptyGameList
              title="Ой, список ваших игр пуст!"
              description="Чтобы добавить игру, создайте её, используя кнопку ниже или отправьте заявку на участие в других играх в разделе «Доступные игры»"
              showButton={false}
            />
          ) : (
            <div className={styles.list}>
              {appliedGrouped.map((entry, idx) => {
                if (entry.type === "year") {
                  return <YearSeparator key={`year-${entry.year}`} year={entry.year!} />;
                }
                return (
                  <DateSection key={`date-${entry.dateKey}-${idx}`} dateLabel={entry.dateLabel!}>
                    {entry.items!.map((game) => {
                      const isCreatedByUser = Boolean(
                        currentUserId && game.creator_id === currentUserId
                      );
                      return (
                        <GamesItem
                          key={game.id}
                          item={game}
                          isCreateGame={isCreatedByUser}
                          isAppliedGame={!isCreatedByUser}
                          setIsRequiredAuthOpened={setIsRequiredAuthOpened}
                          handleCancelModalVisible={
                            !isCreatedByUser ? handleCancelModalOpen : undefined
                          }
                        />
                      );
                    })}
                  </DateSection>
                );
              })}
            </div>
          )}
        </>
      )}

      {activeSubTab === "created" && (
        <>
          {filteredCreated.length === 0 ? (
            <EmptyGameList
              title="Ой, список ваших игр пуст!"
              description="Чтобы создать игру, нажмите кнопку ниже"
              showButton={false}
            />
          ) : (
            <div className={styles.list}>
              {createdGrouped.map((entry, idx) => {
                if (entry.type === "year") {
                  return <YearSeparator key={`year-${entry.year}`} year={entry.year!} />;
                }
                return (
                  <DateSection key={`date-${entry.dateKey}-${idx}`} dateLabel={entry.dateLabel!}>
                    {entry.items!.map((game) => (
                      <GamesItem
                        key={game.id}
                        item={game}
                        isCreateGame={true}
                        setIsRequiredAuthOpened={setIsRequiredAuthOpened}
                      />
                    ))}
                  </DateSection>
                );
              })}
            </div>
          )}
        </>
      )}

      <div className={styles.stickyButtonContainer}>
        <Button
          onClickFunc={() => {
            if (activeSubTab === "enjoying") {
              navigate("/padel/games/available");
            } else {
              openModal({ name: "createNewGame" });
            }
          }}
          newStyles={styles.stickyButton}
        >
          {activeSubTab === "enjoying" ? "Доступные игры" : "Создать игру"}
        </Button>
      </div>

      <CancelRequestModal
        isOpen={cancelModalGameId !== null}
        setIsOpen={handleCancelModalClose}
        onConfirm={handleCancelApplication}
        isLoading={isCancelling}
      />
    </>
  );
};
